import React, { useEffect } from 'react'
import CurrencyInput from 'tennders-common/form/components/new-inputs/CurrencyInput'
import useMultipleCostInvoiceRow from 'tennders-finance/cost-invoice/hooks/useMultipleCostInvoiceRow'
import GHref from 'tennders-market/common/components/element/GHref'
import TransactionSelectInput from 'tennders-finance/cost-invoice/components/TransactionSelectInput'
import GCurrency from 'tennders-market/common/components/element/GCurrency'
import PercentageInput from 'tennders-common/form/components/new-inputs/PercentageInput'
import TextInput from 'tennders-common/form/components/new-inputs/TextInput'
import { TransactionType } from 'tennders-transactions/types/TransactionType'
import { get, round } from 'lodash'
import useTranslation from 'next-translate/useTranslation'
import useShowDoc from 'tennders-documents/hooks/useShowDoc'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons'
import AgreedPriceField from './AgreedPriceField'

type Props = {
  index: number,
  removeLine ?: (index: number) => void,
  companyId ?: number,
  initialErrors ?: Record<string, any>,
  errors ?: Record<string, any>,
  defaultVatPercentage ?: number|string,
  onFormChange ?: (index: number, form: Record<string, any>) => void,
  saved ?: boolean,
  keyIndex ?: number,
  selectedTransaction ?: TransactionType,
  reversedVat ?: boolean,
  selectedPaymentTerm ?: any,
  lineCount ?: number
}

const MultipleCostInvoiceRow = (props: Props) => {
  const { t } = useTranslation()
  const { setDocument } = useShowDoc()
  const {
    index = 0,
    removeLine,
    companyId,
    onFormChange,
    initialErrors,
    errors,
    defaultVatPercentage = '',
    reversedVat = false,
    saved = false,
    keyIndex,
    lineCount,
    selectedTransaction,
    selectedPaymentTerm
  } = props
  const {
    agreedPrice,
    initializeSalesInvoice,
    salesInvoiceLoading,
    salesInvoiceModal,
    transaction,
    onTransactionChange,
    onChange,
    form,
    toBePaid,
    setTransaction
  } = useMultipleCostInvoiceRow({
    vatPercentage: defaultVatPercentage !== '' ? defaultVatPercentage as number : undefined,
    onFormChange,
    index: keyIndex,
    initialErrors
  })

  useEffect(() => {
    if (selectedTransaction) {
      onTransactionChange(selectedTransaction)
    }
  }, [selectedTransaction])

  return (
    <>
      <tr style={{ opacity: saved ? 0.4 : 1, cursor: 'not-allowed' }}>
        <td>
          <GHref
            className="cursor-pointer link-mid-gray"
            style={{ fontSize: 20, lineHeight: '0.6' }}
            onClick={() => (lineCount < 2 ? window.alert(t('costInvoice:you_should_have_at')) : removeLine(keyIndex))}
          >
            <FontAwesomeIcon icon={faXmark} fontSize={20} />
          </GHref>
        </td>
        <td>{index + 1}</td>
        <td>
          <div className="d-flex flex-col pos-rel">
            <TransactionSelectInput
              selectedOptionLabel={transaction?.referenceNumber}
              transactionId={transaction?.id}
              companyId={companyId}
              companyType="carrier"
              error={get(errors, '0.errors.transaction') && !get(transaction, 'referenceNumber') && get(errors, '0.errors.transaction')}
              onTransactionChange={onTransactionChange}
              showError
              disabled={!companyId}
            />
          </div>
        </td>
        <td className="white-space-nowrap">
          <AgreedPriceField
            setTransaction={setTransaction}
            agreedPrice={agreedPrice}
            transaction={transaction}
          />
        </td>
        <td className={'white-space-nowrap ' + (transaction?.matchedTruck?.paymentTerm?.days !== selectedPaymentTerm?.days ? 'color-red' : '')}>
          {transaction?.matchedTruck?.paymentTerm?.days} Days
        </td>
        <td className="white-space-nowrap">
          <CurrencyInput
            allowNegativeValue={false}
            error={errors?.priceOnInvoice}
            onSet={(value) => onChange('priceOnInvoice', value)}
            value={round(form.priceOnInvoice, 2)}
            className="form-input smaller w-100"
          />
        </td>
        <td>
          <PercentageInput
            allowNegativeValue={false}
            error={errors?.vatPercentage}
            onSet={(value) => onChange('vatPercentage', value)}
            value={form.vatPercentage || 0}
            className="form-input smaller w-auto"
          />
        </td>
        <td>
          <TextInput
            value={form.concept}
            error={errors?.concept}
            onSet={(value) => onChange('concept', value)}
            type="text"
            className="form-input smaller w-100"
          />
        </td>
        <td className="white-space-nowrap">
          €{agreedPrice ? GCurrency({ value: agreedPrice - form.priceOnInvoice }) : ''}
        </td>
        <td className="white-space-nowrap">
          {toBePaid ? GCurrency({ value: toBePaid }) : ''}
        </td>
        <td>
          <GHref onClick={() => (transaction?.matchedTruck?.cmrDocument ? setDocument(transaction?.matchedTruck?.cmrDocument) : null)}>
            <u>{transaction?.matchedTruck?.cmrDocument?.name || '-'}</u>
          </GHref>
        </td>
        <td>{transaction?.approvedSalesInvoices?.length ? t('costInvoice:yes') : t('costInvoice:no') }</td>
        <td>
          {!transaction && '-'}
          {transaction ? (
            <div className="d-flex row w-100">
              {salesInvoiceLoading && (
                <span className="mr-8">
                  <FontAwesomeIcon icon={faSpinner} spin fontSize={14} />
                </span>
              )}
              {transaction?.salesInvoice?.id ? transaction?.approvedSalesInvoices?.map((salesInvoice) => (
                <p key={salesInvoice.id}>
                  <GHref href={salesInvoice?.documentUrl} target="_blank">
                    <u>
                      {`[${salesInvoice?.uniqueFileName}], `}
                    </u>
                  </GHref>
                </p>
              )) : (
                <GHref onClick={() => initializeSalesInvoice(transaction)}>
                  {t('salesInvoice:add_sales_invoice ')}
                </GHref>
              )}
            </div>
          ) : '-'}
        </td>
      </tr>
      {salesInvoiceModal}
    </>
  )
}

export default React.memo(MultipleCostInvoiceRow)
